<template>
  <div>
    <div class="bg-grey-lighter border flex items-center relative z-10" v-if="mode === 'edit'">
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Custom Background Image" @click="openMediaManager">
        <i class="fas fa-image fa-fw"></i>
      </button>
      <button v-if="content.background_image" class="px-2 py-3 bg-grey-lighter hover:bg-grey-light" title="Remove Custom Background Image" @click="content.background_image = null">
        <i class="fas fa-trash-alt fa-fw"></i>
      </button>
      <button class="px-2 py-3 bg-grey-lighter hover:bg-grey-light border-l" title="Toggle Property Details" @click="content.show_property_details = !content.show_property_details">
        <i class="fas fa-file-alt fa-fw"></i> <i class="fas" :class="content.show_property_details ? 'fa-check-circle text-green-dark' : 'fa-times-circle text-red-dark'"></i>
      </button>
      <button class="px-2 py-3 bg-gray-200 hover:bg-gray-300 border-l" title="Image Display" @click="content.image_style = content.image_style === 'contain' ? 'cover': 'contain'">
        <i class="fas fa-fw" :class="content.image_style === 'contain' ? 'fa-expand' : 'fa-compress'"></i>
      </button>
    </div>

    <div class="flex-1 bg-grey-lightest flex flex-col justify-between relative overflow-hidden min-h-screen">
      <div class="absolute pin bg-center bg-no-repeat bg-cover animate-zoom bg-white" :class="content.image_style === 'contain' ? 'bg-contain' : 'bg-cover'" v-if="backgroundImage" :style="{
          backgroundImage: `url('${backgroundImage}')`
        }"></div>
      <editor ref="editor" :editable="mode === 'edit'" :extensions="extensions" @update="onUpdate" class="z-10">
        <div slot="content" slot-scope="props" class="bg-white-90 rounded m-6 w-4/5 sm:w-1/2 xl:w-1/4 text-content p-3 shadow ezine-editor"></div>
      </editor>

      <div class="hidden md:block bg-white-90 rounded m-6 w-4/5 lg:w-1/2 xl:w-1/4 shadow overflow-hidden z-10" v-if="showPropertyDetails">
        <div class="p-3 bg-grey">Feature Property</div>
        <div class="p-3">
          <h2>{{ featuredProperty.display_address }}</h2>
          <p>
              <small v-if="featuredProperty.bedrooms" class="text-gray-500">{{ featuredProperty.bedrooms }} {{ featuredProperty.bedrooms === 1 ? 'bedroom' : 'bedrooms'  }}</small>
              <small v-if="featuredProperty.bathrooms" class="text-gray-500"> - {{ featuredProperty.bathrooms }} {{ featuredProperty.bathrooms === 1 ? 'bathroom' : 'bathrooms' }}</small>
          </p>
          <hr class="my-6 border-grey border-t" />
          <p class="text-lg">{{ featuredProperty.price | gbp }}</p>
          <small class="block">Added: {{ featuredProperty.created_at | date }}</small>
        </div>
      </div>
    </div>
    <ezine-media-manager ref="mediaManager" :assignable-id="selectedOffice.client_id" @selected="content.background_image = $event" />
  </div>
</template>

<script>
import EzineMediaManager from '../EzineMediaManager.vue'
import { Editor } from 'tiptap'
import {
  HardBreakNode,
  LinkMark,
  BoldMark,
  ItalicMark,
  UnderlineMark,
  ImageNode,
  HeadingNode,
  ListItemNode,
  BulletListNode,
  OrderedListNode,
  HistoryExtension,
  PlaceholderExtension
} from 'tiptap-extensions'
import { v2Api } from '@/http'
import { mapState } from 'vuex'

export default {
  components: { EzineMediaManager, Editor },
  props: {
    value: Object,
    mode: String
  },
  data () {
    return {
      featuredProperty: null,
      extensions: [
        new HardBreakNode(),
        new LinkMark(),
        new BoldMark(),
        new ItalicMark(),
        new UnderlineMark(),
        new ImageNode(),
        new HeadingNode({ maxLevel: 3 }),
        new ListItemNode(),
        new BulletListNode(),
        new OrderedListNode(),
        new HistoryExtension(),
        new PlaceholderExtension()
      ]
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    content: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    featuredPropertyImage () {
      const media = this.featuredProperty ? this.featuredProperty.media.find((media) => media.type_id === 0) : null
      return media ? media.hd_url : null
    },
    backgroundImage () {
      return this.content.background_image || this.featuredPropertyImage || null
    },
    showPropertyDetails () {
      return this.content.show_property_details && this.featuredProperty && [0, 1, 2].includes(this.content.type_id)
    }
  },
  mounted () {
    this.$refs.editor.setContent(this.content.intro)
  },
  created () {
    this.getFeaturedProperty()
  },
  methods: {
    openMediaManager () {
      this.$refs.mediaManager.open()
    },
    getFeaturedProperty () {
      v2Api.get('property', {
        params: {
          client_id: this.selectedOffice.client_id,
          images_count__gte: 1,
          rental: false,
          order_by: 'price',
          sort: 'desc',
          per_page: 1
        }
      }).then((response) => {
        this.featuredProperty = response.data.data ? response.data.data[0] : null
      })
    },
    onUpdate ({ getHTML }) {
      let html = getHTML()
      if (/^<p>\s*<\/p>$/i.test(html)) {
        html = ''
      }

      this.content.intro = html
    }
  }
}
</script>

<style>
.bg-white-90 {
  background-color: rgba(255, 255, 255, .9);
}
</style>
